import { useMemo } from 'react';
import { stackOrderAscending, stackOffsetExpand, stackOrderNone, stackOffsetNone } from 'd3';
import { TradeFlow, TradeDirection, LocationDetailLevel } from "@atlas-int/data/types";
import { OverTimeOrderingOption, OverTimeLayoutOption, determineScaledTradeValueSelector } from '../constants';
import { ChartDataResult, ChartQueryVariables, RawSeriesData, TradeValueScaledByYAxisOption } from '../types';
import type { YearRangeForProductClass } from '../types';
import type { ProductMetadataBundle, MetadataFetchState } from "@atlas-int/data/types";
import type { LocationMetadataPayload, LocationYearDatum, DeflatorDatum } from "@atlas-int/data/types";
import { transformProductsView } from './transformProductsView';
import { transformMarketsView } from './transformMarketsView';

import type { TradeDataItem as SharedTradeDataItem } from "@atlas-int/data/types";
import { TradeDirectionSelectorString, UIView } from "@atlas-int/data";

/** A trade data item with dynamic properties from merges and transforms */
interface TradeDataItem extends SharedTradeDataItem {
  year: number;
  partnerId?: string;
  topLevelParent?: string;
}

/** Input data wrapper from GraphQL query */
interface InputDataWrapper {
  data: TradeDataItem[];
}

// =============================================================================
// Main hook
// =============================================================================

interface UseChartDataTransformationProps {
  inputData: InputDataWrapper | unknown;
  queryVariables: ChartQueryVariables;
  yearRangeForProductClass: YearRangeForProductClass;
  hiddenCategories: string[];
  overTimeLayout: OverTimeLayoutOption;
  overTimeOrdering: OverTimeOrderingOption;
  locationLevel: LocationDetailLevel;
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  tradeDirectionSelector: TradeDirectionSelectorString;
  locationMetadata: MetadataFetchState<LocationMetadataPayload>;
  productMetadata: ProductMetadataBundle;
  locationYearMetadata: MetadataFetchState<LocationYearDatum[]>;
  deflatorsMetadata: MetadataFetchState<DeflatorDatum[]>;
  setYAxisDomain: (domain: { min: number; max: number }) => void;
}

export const useChartDataTransformation = ({
  inputData,
  queryVariables,
  yearRangeForProductClass,
  hiddenCategories,
  overTimeLayout,
  overTimeOrdering,
  locationLevel,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector,
  locationMetadata: locationMetadataState,
  productMetadata,
  locationYearMetadata: locationYearMetadataState,
  deflatorsMetadata: deflatorsMetadataState,
  setYAxisDomain,
}: UseChartDataTransformationProps): ChartDataResult => {
  const {
    view,
    productClass,
    productLevel,
    exporter,
    importer,
    yearMin,
    yearMax,
    yAxis: yAxisScalingOption,
  } = queryVariables;

  const locationMetadataStatus = locationMetadataState.status;
  const locationMetadata = locationMetadataState.status === "success" ? locationMetadataState.data : undefined;
  const locationYearMetadataStatus = locationYearMetadataState.status;
  const locationYearMetadata = locationYearMetadataState.status === "success" ? locationYearMetadataState.data : undefined;
  const deflatorsMetadataStatus = deflatorsMetadataState.status;
  const deflatorsMetadata = deflatorsMetadataState.status === "success" ? deflatorsMetadataState.data : undefined;

  return useMemo(() => {
    let seriesData: RawSeriesData[] | undefined = undefined;
    let totalSum: number | undefined = undefined;
    let stackKeysSet: Set<string>;
    let noDataToDisplay: boolean = false;

    const scaledTradeValueSelector: TradeValueScaledByYAxisOption =
      determineScaledTradeValueSelector(yAxisScalingOption);

    const stackOrderFunction =
      overTimeOrdering === OverTimeOrderingOption.Totals
        ? stackOrderAscending
        : stackOrderNone;

    const stackOffsetFunction =
      overTimeLayout === OverTimeLayoutOption.Share
        ? stackOffsetExpand
        : stackOffsetNone;

    let { data } = inputData as InputDataWrapper;

    if (!data || data.length === 0) {
      return {
        seriesData: undefined,
        totalSum: undefined,
        stackKeysSet: new Set<string>(),
        noDataToDisplay: true,
        scaledTradeValueSelector,
      };
    }

    if (view === UIView.Products) {
      const result = transformProductsView({
        data,
        productClass,
        productLevel,
        productMetadata,
        deflatorsMetadataStatus,
        deflatorsMetadata,
        locationYearMetadataStatus,
        locationYearMetadata,
        hiddenCategories,
        yearMin: yearMin!,
        yearMax: yearMax!,
        yearRangeForProductClass,
        tradeFlow,
        tradeDirection,
        tradeDirectionSelector,
        scaledTradeValueSelector,
        yAxisScalingOption,
        stackOrderFunction,
        stackOffsetFunction,
        setYAxisDomain,
      });
      seriesData = result.seriesData;
      totalSum = result.totalSum;
      noDataToDisplay = result.noDataToDisplay;
      stackKeysSet = result.stackKeysSet!;
    } else if (view === UIView.Markets) {
      const result = transformMarketsView({
        data,
        exporter,
        importer,
        locationMetadataStatus,
        locationMetadata,
        deflatorsMetadataStatus,
        deflatorsMetadata,
        locationLevel,
        hiddenCategories,
        yearMin: yearMin!,
        yearMax: yearMax!,
        yearRangeForProductClass,
        tradeFlow,
        tradeDirection,
        tradeDirectionSelector,
        scaledTradeValueSelector,
        stackOrderFunction,
        stackOffsetFunction,
        setYAxisDomain,
      });
      seriesData = result.seriesData;
      totalSum = result.totalSum;
      noDataToDisplay = result.noDataToDisplay;
      stackKeysSet = result.stackKeysSet!;
    }
    return {
      seriesData,
      totalSum,
      stackKeysSet,
      noDataToDisplay,
      scaledTradeValueSelector,
    };
  }, [
    yAxisScalingOption,
    overTimeOrdering,
    overTimeLayout,
    inputData,
    view,
    productClass,
    productMetadata,
    deflatorsMetadataState,
    locationYearMetadataState,
    tradeFlow,
    tradeDirection,
    productLevel,
    yearRangeForProductClass,
    hiddenCategories,
    yearMin,
    yearMax,
    tradeDirectionSelector,
    setYAxisDomain,
    locationMetadataState,
    exporter,
    importer,
    locationLevel,
  ]);
};
